import { useTranslation } from '../../../shared/i18n';

import React, { useState, useEffect } from 'react';
import {
    Table, Card, Button, Space, Input, message, Modal, Form,
    Typography, Tag, Divider, Empty, Tooltip, Alert, Progress
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, ExclamationCircleOutlined,
    SearchOutlined, LinkOutlined, InfoCircleOutlined
} from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dnsService from '../../services/dnsService';

import { userPath } from '../../../shared/config/paths';
import { rowLink, ROW_LINK_CLASS } from '../../../shared/utils/rowLink';

const { Title, Text } = Typography;
const { confirm } = Modal;

const DnsCidr = () => {
    const { t } = useTranslation();
    const [cidrs, setCidrs] = useState([]);
    const [loading, setLoading] = useState(false);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingCidr, setEditingCidr] = useState(null);
    const [form] = Form.useForm();
    const [searchValue, setSearchValue] = useState('');
    const [quota, setQuota] = useState(null);
    const navigate = useNavigate();

    // 获取CIDR列表
    const fetchCidrs = async () => {
        setLoading(true);
        try {
            const result = await dnsService.getCidrs(searchValue);
            if (result.success) {
                setCidrs(result.data);
                if (result.quota) {
                    setQuota(result.quota);
                }
            } else {
                message.error(result.error || t('Failed to retrieve CIDR lists'));
            }
        } catch (error) {
            message.error(t('Failed to retrieve CIDR lists: {{message}}', { message: error.message }));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchCidrs();
    }, [searchValue]);

    // 显示添加CIDR模态框
    const showAddModal = () => {
        setEditingCidr(null);
        form.resetFields();
        setIsModalVisible(true);
    };

    // 显示编辑CIDR模态框
    const showEditModal = (cidr) => {
        setEditingCidr(cidr);
        form.setFieldsValue({
            name: cidr.name,
            description: cidr.description || ''
        });
        setIsModalVisible(true);
    };

    // 处理模态框确认
    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            if (editingCidr) {
                // 更新CIDR
                const result = await dnsService.updateCidr({
                    id: editingCidr.id,
                    ...values
                });

                if (result.success) {
                    message.success(t('CIDR updated'));
                    setIsModalVisible(false);
                    fetchCidrs();
                } else {
                    message.error(result.error || t('Failed to update CIDR'));
                }
            } else {
                // 创建新CIDR
                const result = await dnsService.createCidr(values);

                if (result.success) {
                    message.success(t('CIDR created'));
                    setIsModalVisible(false);
                    fetchCidrs();
                } else {
                    message.error(result.error || t('Failed to create CIDR'));
                }
            }
        } catch (error) {
            console.log('Validation failed:', error);
        } finally {
            setLoading(false);
        }
    };

    // 删除CIDR
    const deleteCidr = (cidr) => {
        confirm({
            title: t('Confirm deletion'),
            icon: <ExclamationCircleOutlined />,
            content: t('Delete CIDR list "{{name}}"? All associated IP ranges will also be deleted. This action cannot be undone.', { name: cidr.name }),
            okText: t('Delete'),
            okType: 'danger',
            cancelText: t('Cancel'),
            onOk: async () => {
                try {
                    setLoading(true);
                    const result = await dnsService.deleteCidr(cidr.id);

                    if (result.success) {
                        message.success(t('CIDR deleted'));
                        fetchCidrs();
                    } else if (Array.isArray(result.records) && result.records.length > 0) {
                        Modal.error({
                            title: result.error,
                            content: (
                                <ul style={{ paddingLeft: 18, margin: 0 }}>
                                    {result.records.slice(0, 10).map(r => (
                                        <li key={r.record_id}>{(r.name === '@' ? '' : r.name + '.') + r.zone} {r.type}</li>
                                    ))}
                                    {result.records.length > 10 && <li>…</li>}
                                </ul>
                            ),
                        });
                    } else {
                        message.error(result.error || t('Failed to delete CIDR list'));
                    }
                } catch (error) {
                    message.error(t('Failed to delete CIDR list: {{message}}', { message: error.message }));
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    // 处理搜索
    const handleSearch = (value) => {
        setSearchValue(value);
    };

    // 表格列配置
    const columns = [
        {
            title: t('Name'),
            dataIndex: 'name',
            key: 'name',
            render: text => <a>{text}</a>,
        },
        {
            title: t('Description'),
            dataIndex: 'description',
            key: 'description',
            render: text => text || '-',
            ellipsis: true,
        },
        {
            title: t('IP entry count'),
            dataIndex: 'entry_count',
            key: 'entry_count',
            render: count => {
                if (!count) return 0;
                return (
                    <Tooltip title={t('View details')}>
                        <Tag color="blue" style={{ cursor: 'pointer' }}>{t('{{count}} IP ranges', { count })}</Tag>
                    </Tooltip>
                );
            }
        },
        {
            title: t('Usage status'),
            key: 'usage',
            render: (_, record) => {
                const count = record.dns_usage_count || 0;
                if (count === 0) return <Tag color="default">{t('Unused')}</Tag>;
                return <Tag color="green">{t('{{count}} records in use', { count })}</Tag>;
            }
        },
        {
            title: t('Created at'),
            dataIndex: 'created_at',
            key: 'created_at',
            render: text => new Date(text).toLocaleString()
        },
        {
            title: t('Updated at'),
            dataIndex: 'updated_at',
            key: 'updated_at',
            render: text => new Date(text).toLocaleString()
        },
        {
            title: t('Actions'),
            key: 'action',
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="primary"
                        size="small"
                        onClick={() => navigate(userPath(`/dns/cidr/${record.id}`))}
                        icon={<LinkOutlined />}
                    >
                        {record.editable === false ? t('View IP ranges') : t('Manage IP ranges')}
                    </Button>
                    <Tooltip title={record.editable === false ? t('System preset, cannot be edited') : ''}>
                        <Button
                            type="default"
                            size="small"
                            onClick={() => showEditModal(record)}
                            icon={<EditOutlined />}
                            disabled={record.editable === false}
                        >
                            {t('Edit')}
                        </Button>
                    </Tooltip>
                    <Tooltip title={record.editable === false ? t('System preset, cannot be deleted') : ''}>
                        <Button
                            type="default"
                            danger
                            size="small"
                            onClick={() => deleteCidr(record)}
                            icon={<DeleteOutlined />}
                            disabled={record.editable === false}
                        >
                            {t('Delete')}
                        </Button>
                    </Tooltip>
                </Space>
            ),
        },
    ];

    // 计算配额百分比
    const quotaPercentage = quota && quota.limit > 0 ? Math.round((quota.current / quota.limit) * 100) : 0;
    const isQuotaExceeded = quota && !quota.is_unlimited && quota.current >= quota.limit;

    return (
        <div>
            <Title level={2}>{t('CIDR lists')}</Title>

            {/* 配额显示 */}
            {quota && (
                <Alert
                    style={{ marginBottom: 16 }}
                    type={isQuotaExceeded ? 'error' : quotaPercentage >= 80 ? 'warning' : 'info'}
                    showIcon
                    message={
                        <Space>
                            <Text>{t('CIDR quota usage:')}</Text>
                            {quota.is_unlimited ? (
                                <Tag color="green">{t('Unlimited')}</Tag>
                            ) : (
                                <>
                                    <Text strong>{quota.current} / {quota.limit}</Text>
                                    <Progress
                                        percent={quotaPercentage}
                                        size="small"
                                        style={{ width: 100 }}
                                        status={isQuotaExceeded ? 'exception' : quotaPercentage >= 80 ? 'active' : 'normal'}
                                        showInfo={false}
                                    />
                                </>
                            )}
                            <Text type="secondary">{t('(Quota is the sum of CIDR limits across all DNS plans)')}</Text>
                        </Space>
                    }
                />
            )}

            <Card
                title={
                    <Space size="middle">
                        <span>{t('CIDR lists')}</span>
                        <Tooltip title={t('CIDR groups organize IP addresses and can be referenced in DNS records for IP-based routing.')}>
                            <InfoCircleOutlined style={{ color: '#1890ff' }} />
                        </Tooltip>
                    </Space>
                }
                extra={
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={showAddModal}
                        disabled={isQuotaExceeded}
                    >
                        {t('Add CIDR list')}
                    </Button>
                }
            >
                <div style={{ marginBottom: 16 }}>
                    <Input.Search
                        placeholder={t('Search CIDR name or description')}
                        allowClear
                        enterButton={<Button icon={<SearchOutlined />}>{t('Search')}</Button>}
                        onSearch={handleSearch}
                        style={{ maxWidth: 400 }}
                    />
                </div>

                <Table
                    onRow={rowLink(navigate, (r) => `/dns/cidr/${r.id}`)}
                    rowClassName={() => ROW_LINK_CLASS}
                    columns={columns}
                    dataSource={cidrs}
                    rowKey="id"
                    loading={loading}
                    scroll={{ x: 'max-content' }}
                    locale={{
                        emptyText: <Empty description={t('No CIDR groups yet')} />
                    }}
                    pagination={{
                        showSizeChanger: true,
                        showTotal: total => t('{{total}} CIDR groups total', { total })
                    }}
                />
            </Card>

            {/* 添加/编辑CIDR模态框 */}
            <Modal
                title={editingCidr ? t('Edit CIDR: {{name}}', { name: editingCidr.name }) : t('Add CIDR list')}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={() => setIsModalVisible(false)}
                confirmLoading={loading}
            >
                <Form
                    form={form}
                    layout="vertical"
                >
                    <Form.Item
                        name="name"
                        label={t('CIDR name')}
                        rules={[{ required: true, message: t('Please enter a CIDR name!') }]}
                    >
                        <Input placeholder={t('E.g. China Telecom')} />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label={t('Description (optional)')}
                    >
                        <Input.TextArea
                            rows={3}
                            placeholder={t('E.g. China Telecom IP ranges for DNS responses to China Telecom users')}
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default DnsCidr;
